import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useWebSocketContext } from './WebSocketContext';
import { NotificationMessage, WebSocketMessage } from '../hooks/useWebSocket';
import NotificationToast from '../components/notifications/NotificationToast';
import { showAlert } from '../utils/alert';
import notificationService from '../services/notificationService';

export interface NotificationContextType {
  // WebSocket state
  isConnected: boolean;
  isConnecting: boolean;
  connectionError: string | null;

  // Notifications
  notifications: NotificationMessage[];
  unreadCount: number;
  toastNotifications: NotificationMessage[];

  // WebSocket actions
  connect: () => void;
  disconnect: () => void;
  sendMessage: (message: WebSocketMessage) => void;

  // Notification actions
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  clearNotifications: () => void;
  removeNotification: (notificationId: string) => void;
  addNotification: (notification: Omit<NotificationMessage, 'id' | 'timestamp' | 'read'>) => void;
  showToast: (notification: Omit<NotificationMessage, 'id' | 'timestamp' | 'read'>) => void;
  removeToast: (notificationId: string) => void;

  // Settings
  notificationSettings: NotificationSettings;
  updateNotificationSettings: (settings: Partial<NotificationSettings>) => void;
}

export interface NotificationSettings {
  enableWebSocket: boolean;
  enableToasts: boolean;
  enableSound: boolean;
  toastDuration: number;
  categories: {
    system: boolean;
    rental: boolean;
    contract: boolean;
    payment: boolean;
    maintenance: boolean;
  };
  types: {
    info: boolean;
    success: boolean;
    warning: boolean;
    error: boolean;
  };
}

const defaultSettings: NotificationSettings = {
  enableWebSocket: true,
  enableToasts: true,
  enableSound: false,
  toastDuration: 5000,
  categories: {
    system: true,
    rental: true,
    contract: true,
    payment: true,
    maintenance: true
  },
  types: {
    info: true,
    success: true,
    warning: true,
    error: true
  }
};

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export interface NotificationProviderProps {
  children: React.ReactNode;
  websocketUrl?: string;
  autoConnect?: boolean;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  // Notification state — managed here instead of in useWebSocket
  const [notifications, setNotifications] = useState<NotificationMessage[]>([]);
  const [toastNotifications, setToastNotifications] = useState<NotificationMessage[]>([]);
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>(() => {
    const saved = localStorage.getItem('notification_settings');
    return saved ? { ...defaultSettings, ...JSON.parse(saved) } : defaultSettings;
  });

  // Keep notificationSettings accessible inside message handler without triggering re-subscribe
  const notificationSettingsRef = useRef(notificationSettings);
  useEffect(() => {
    notificationSettingsRef.current = notificationSettings;
  }, [notificationSettings]);

  // Persist settings to localStorage
  useEffect(() => {
    localStorage.setItem('notification_settings', JSON.stringify(notificationSettings));
  }, [notificationSettings]);

  // Get shared WebSocket connection from context
  const {
    isConnected,
    isConnecting,
    connectionError,
    connect,
    disconnect,
    sendMessage,
    subscribe,
  } = useWebSocketContext();

  // Computed unread count
  const unreadCount = notifications.filter(n => !n.read).length;

  // ---- Notification state management ----

  const markAsRead = useCallback((notificationId: string) => {
    // Optimistic local update
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );

    // Persist to API for DB-backed notifications (numeric IDs)
    const numericId = Number(notificationId);
    if (!isNaN(numericId) && numericId > 0) {
      notificationService.markAsRead(numericId).catch((error) => {
        console.error('Failed to persist markAsRead to API:', error);
        // KD3 S2570 (NEG-NOTIF-004): 404 = ใบถูกลบไปแล้ว (อีกแท็บ/อุปกรณ์) → ถอดออกจากรายการ
        // และบอกกระดิ่งให้ดึงใหม่ แทนการ revert เป็น unread ซึ่งทิ้งแถวผีไว้
        if (error?.response?.status === 404) {
          setNotifications(prev => prev.filter(n => n.id !== notificationId));
          window.dispatchEvent(new Event('notifications-updated'));
          return;
        }
        // Revert on failure
        setNotifications(prev =>
          prev.map(n => n.id === notificationId ? { ...n, read: false } : n)
        );
      });
    }
  }, []);

  const markAllAsRead = useCallback(() => {
    // Track which IDs were unread before marking, for targeted revert
    let unreadIds: Set<string> = new Set();
    setNotifications(prev => {
      unreadIds = new Set(prev.filter(n => !n.read).map(n => n.id));
      return prev.map(n => ({ ...n, read: true }));
    });

    // Persist to API — revert only affected IDs on failure (preserves new notifications)
    notificationService.markAllAsRead().catch((error) => {
      console.error('Failed to persist markAllAsRead to API:', error);
      setNotifications(current =>
        current.map(n => unreadIds.has(n.id) ? { ...n, read: false } : n)
      );
    });
  }, []);

  const clearNotifications = useCallback(() => {
    // BUG-NOTI-CLEAR-NOOP FIX: เดิมล้างแค่ local state ไม่เรียก API —
    // polling รอบถัดไปดึงกลับมาครบ ทำให้ปุ่ม "ลบทั้งหมด" ไม่มีผลจริง
    let previous: NotificationMessage[] = [];
    setNotifications(prev => {
      previous = prev;
      return [];
    });

    notificationService.clearAll().catch((error) => {
      console.error('Failed to persist clearNotifications to API:', error);
      // Revert on failure
      setNotifications(previous);
    });
  }, []);

  const removeNotification = useCallback((notificationId: string) => {
    // BUG-S1368 FIX: เดิมลบแค่ local state ไม่เรียก API (คลาสเดียวกับ BUG-NOTI-CLEAR-NOOP)
    // — poll/refetch รอบถัดไปดึงกลับมา ทำให้ปุ่ม × ลบรายตัวไม่มีผลจริง
    let removed: NotificationMessage | undefined;
    setNotifications(prev => {
      removed = prev.find(n => n.id === notificationId);
      return prev.filter(n => n.id !== notificationId);
    });

    // Persist to API for DB-backed notifications (numeric IDs) — WS-only id (random
    // string) ไม่มีแถวใน DB ให้ลบ ข้ามได้
    const numericId = Number(notificationId);
    if (!isNaN(numericId) && numericId > 0) {
      notificationService.deleteNotification(numericId).catch((error) => {
        console.error('Failed to persist removeNotification to API:', error);
        // Revert on failure
        if (removed) {
          const revertItem = removed;
          setNotifications(prev =>
            prev.some(n => n.id === revertItem.id) ? prev : [revertItem, ...prev]
          );
        }
      });
    }
  }, []);

  // ---- Toast helpers ----

  const showToast = useCallback((notification: Omit<NotificationMessage, 'id' | 'timestamp' | 'read'>) => {
    const toastNotification: NotificationMessage = {
      ...notification,
      id: `toast-${Date.now()}-${Math.random()}`,
      timestamp: new Date().toISOString(),
      read: false
    };
    setToastNotifications(prev => [...prev, toastNotification]);
  }, []);

  const removeToast = useCallback((notificationId: string) => {
    setToastNotifications(prev => prev.filter(n => n.id !== notificationId));
  }, []);

  // ---- Manual notification (not from WS) ----

  const addNotification = useCallback((notification: Omit<NotificationMessage, 'id' | 'timestamp' | 'read'>) => {
    const settings = notificationSettingsRef.current;

    if (!settings.categories[notification.category] || !settings.types[notification.type]) {
      return;
    }

    if (settings.enableToasts) {
      showToast(notification);
    }

    if (settings.enableSound) {
      playNotificationSound(notification.type);
    }
  }, [showToast]); // showToast is stable

  // ---- WebSocket message handler ----

  useEffect(() => {
    const handleMessage = (message: any) => {
      // Handle maintenance-related messages
      if (
        message.type === 'maintenance_new' ||
        message.type === 'maintenance_update' ||
        message.type === 'maintenance_assigned' ||
        message.type === 'maintenance_completed'
      ) {
        const notification: NotificationMessage = {
          id: message.notification_id?.toString() || `${Date.now()}-${Math.random()}`,
          type: message.priority === 'urgent' || message.priority === 'high' ? 'warning' : 'info',
          title: message.title || 'การแจ้งเตือนซ่อมบำรุง',
          message: message.message || '',
          timestamp: message.timestamp || new Date().toISOString(),
          data: message.data,
          read: false,
          category: 'maintenance',
          reference_type: message.data?.reference_type || 'maintenance',
          reference_id: message.data?.maintenance_id || message.data?.reference_id,
        };

        setNotifications(prev => [notification, ...prev].slice(0, 100));
        // Signal the notification bell to re-fetch its DB unread count so the
        // badge reflects this real-time arrival (bell's dbUnreadCount otherwise
        // only refreshes on mount / dropdown-open).
        window.dispatchEvent(new CustomEvent('notifications-updated'));
        // No showAlert here — MaintenanceAlertContext handles popup/sound for maintenance messages
        return;
      }

      // P-S473-1: ผลอนุมัติ/ปฏิเสธคำขอต่ออายุ — push ตรงถึงผู้ยื่นแบบ real-time
      if (
        message.type === 'rental_extension_approved' ||
        message.type === 'rental_extension_rejected'
      ) {
        const notification: NotificationMessage = {
          id: message.notification_id?.toString() || `${Date.now()}-${Math.random()}`,
          type: message.type === 'rental_extension_rejected' ? 'warning' : 'success',
          title: message.title || 'ผลคำขอต่ออายุ',
          message: message.message || '',
          timestamp: message.timestamp || new Date().toISOString(),
          data: message.data,
          read: false,
          category: 'rental',
          reference_type: message.reference_type || 'rental',
          reference_id: message.reference_id || message.data?.rental_id,
        };
        setNotifications(prev => [notification, ...prev].slice(0, 100));
        window.dispatchEvent(new CustomEvent('notifications-updated'));
        return;
      }

      // Handle general notification messages
      if (message.type === 'notification') {
        const notification: NotificationMessage = {
          id: message.payload?.id || `${Date.now()}-${Math.random()}`,
          type: message.payload?.type || 'info',
          title: message.payload?.title || 'การแจ้งเตือน',
          message: message.payload?.message || '',
          timestamp: message.payload?.timestamp || new Date().toISOString(),
          data: message.payload?.data,
          read: false,
          category: message.payload?.category || 'system',
          reference_type: message.payload?.reference_type || message.payload?.data?.reference_type,
          reference_id: message.payload?.reference_id || message.payload?.data?.reference_id,
        };

        setNotifications(prev => [notification, ...prev].slice(0, 100));
        // Refresh the bell's DB unread count for this real-time arrival.
        window.dispatchEvent(new CustomEvent('notifications-updated'));

        if (notification.type === 'error' || notification.type === 'warning') {
          showAlert(notification.title, notification.message, notification.type);
        }
        return;
      }

      // Handle data_update — dispatch as a DOM event so other components can react
      if (message.type === 'data_update') {
        window.dispatchEvent(new CustomEvent('data_update', { detail: message.payload }));
      }
    };

    return subscribe(handleMessage);
  }, [subscribe]); // subscribe is stable (useCallback with empty deps in WebSocketContext)

  // Send user_activity when WebSocket connects
  useEffect(() => {
    if (isConnected) {
      console.log('NotificationContext: WebSocket connected — sending user activity');
      sendMessage({
        type: 'user_activity',
        payload: {
          action: 'connect',
          timestamp: new Date().toISOString(),
          user: localStorage.getItem('userId') || 'anonymous'
        }
      });
    }
  }, [isConnected, sendMessage]);

  // ---- Sound helper (unchanged) ----

  const playNotificationSound = useCallback((type: NotificationMessage['type']) => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      switch (type) {
        case 'success':
          oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
          oscillator.frequency.setValueAtTime(1000, audioContext.currentTime + 0.1);
          break;
        case 'warning':
          oscillator.frequency.setValueAtTime(600, audioContext.currentTime);
          oscillator.frequency.setValueAtTime(800, audioContext.currentTime + 0.1);
          break;
        case 'error':
          oscillator.frequency.setValueAtTime(400, audioContext.currentTime);
          oscillator.frequency.setValueAtTime(300, audioContext.currentTime + 0.1);
          break;
        default:
          oscillator.frequency.setValueAtTime(500, audioContext.currentTime);
      }

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);
    } catch (error) {
      console.warn('Could not play notification sound:', error);
    }
  }, []);

  // ---- Settings ----

  const updateNotificationSettings = useCallback((settings: Partial<NotificationSettings>) => {
    setNotificationSettings(prev => ({ ...prev, ...settings }));
  }, []);

  const contextValue: NotificationContextType = {
    // WebSocket state
    isConnected,
    isConnecting,
    connectionError,

    // Notifications
    notifications,
    unreadCount,
    toastNotifications,

    // WebSocket actions
    connect,
    disconnect,
    sendMessage,

    // Notification actions
    markAsRead,
    markAllAsRead,
    clearNotifications,
    removeNotification,
    addNotification,
    showToast,
    removeToast,

    // Settings
    notificationSettings,
    updateNotificationSettings
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}

      {/* Toast Notifications Container */}
      <div className="fixed inset-0 pointer-events-none z-50">
        {toastNotifications.map((notification) => (
          <div key={notification.id} className="pointer-events-auto">
            <NotificationToast
              notification={notification}
              onClose={() => removeToast(notification.id)}
              duration={notificationSettings.toastDuration}
              position="top-right"
            />
          </div>
        ))}
      </div>
    </NotificationContext.Provider>
  );
};

/**
 * BUG-S1146-001 FIX (continuation of BUG-S1144-001 useAuth defensive pattern):
 * Returns safe no-op defaults if NotificationContext is unavailable (Vite HMR race
 * or rare lazy-load timing) instead of throwing. Prevents ErrorBoundary trigger.
 * Logs warning loudly so genuine "forgot NotificationProvider" bugs remain visible.
 */
const NOTIFICATIONS_FALLBACK_DEFAULTS: NotificationContextType = {
  isConnected: false,
  isConnecting: false,
  connectionError: 'Notification context unavailable',
  notifications: [],
  unreadCount: 0,
  toastNotifications: [],
  connect: () => {},
  disconnect: () => {},
  sendMessage: () => {},
  markAsRead: () => {},
  markAllAsRead: () => {},
  clearNotifications: () => {},
  removeNotification: () => {},
  addNotification: () => {},
  showToast: () => {},
  removeToast: () => {},
  notificationSettings: defaultSettings,
  updateNotificationSettings: () => {},
};

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    if (typeof console !== 'undefined') {
      console.warn(
        '[useNotifications] NotificationContext unavailable — using safe defaults. ' +
        'Likely Vite HMR race. If seen in production, ensure <NotificationProvider> wraps the tree.'
      );
    }
    return NOTIFICATIONS_FALLBACK_DEFAULTS;
  }
  return context;
};

export default NotificationContext;
